import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import LeadCaptureForm from '../components/LeadCaptureForm';
import SplashNav from '../components/SplashNav';
import {
  Link2, DollarSign, Wallet, Award, MousePointerClick, Users,
  ArrowRight, Sparkles, TrendingUp, CreditCard,
  ChevronLeft, ChevronRight, Target, BarChart3, Check, Rocket,
  Users as UsersIcon, PiggyBank,
} from 'lucide-react';

const slides = [
  {
    image: 'https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Earn Up to 30% on Every Referral',
    desc: 'Join the affiliate program, get your unique referral code, and earn tiered recurring commission on every conversion.',
    icon: DollarSign,
  },
  {
    image: 'https://images.pexels.com/photos/6285089/pexels-photo-6285089.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Track Every Click to Conversion',
    desc: 'Watch referrals move through your pipeline — from click to signup to paid conversion — in real-time.',
    icon: BarChart3,
  },
  {
    image: 'https://images.pexels.com/photos/5716053/pexels-photo-5716053.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    title: 'Get Paid Your Way',
    desc: 'Payouts via Stripe. Request withdrawals from A$50.',
    icon: Wallet,
  },
];

const affiliateFeatures = [
  { icon: MousePointerClick, title: 'Click Tracking', desc: 'Every referral click tracked in real-time with source attribution.' },
  { icon: Users, title: 'Referral Pipeline', desc: 'Watch referrals move from click to signup to conversion to churn.' },
  { icon: DollarSign, title: 'Tiered Recurring Commission', desc: 'Earn up to 30% on every paid subscription you refer — higher tiers pay more, month after month.' },
  { icon: Wallet, title: 'Flexible Payouts', desc: 'Stripe payouts with a A$50 minimum.' },
  { icon: Award, title: 'Auto Referral Codes', desc: 'Unique codes generated instantly on approval. Share and track.' },
];

const payoutMethods = [
  { icon: CreditCard, label: 'Stripe', desc: 'Automatic transfers to your bank account', color: 'from-violet-500 to-purple-600' },
];

const earningsTiers = [
  { referrals: '10', monthly: '$372', annual: '$4,470', label: '10 referrals / mo' },
  { referrals: '25', monthly: '$931', annual: '$11,175', label: '25 referrals / mo' },
  { referrals: '50', monthly: '$1,863', annual: '$22,350', label: '50 referrals / mo' },
  { referrals: '100', monthly: '$3,725', annual: '$44,700', label: '100 referrals / mo' },
];

const howItWorks = [
  { num: '01', icon: Rocket, title: 'Apply', desc: 'Submit your affiliate application with your preferred payout method.' },
  { num: '02', icon: Link2, title: 'Get Your Code', desc: 'Receive a unique referral code (AME-XXXXXXXX) on approval.' },
  { num: '03', icon: MousePointerClick, title: 'Share & Track', desc: 'Share your code, create custom links, and track every click in real-time.' },
  { num: '04', icon: Wallet, title: 'Get Paid', desc: 'Request payouts from A$50 via Stripe.' },
];

const SplashAffiliate: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <div className="min-h-screen bg-slate-100">
      <SplashNav />
      {/* Page Header */}
      <section className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 px-6 py-10 md:py-14">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-4">
            <Link2 className="h-3.5 w-3.5 text-white" />
            <span className="text-xs font-mono text-white/80 tracking-wider">AURELIAN AFFILIATE PROGRAM</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight">The Affiliate Program</h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">Earn up to 30% recurring commission on every referral you bring to the Aurelian Marketing Engine. Higher-tier plans pay higher rates — and you keep earning on every renewal. Track clicks, conversions, and payouts — all built into the platform.</p>
        </div>
      </section>
      {/* Hero Slideshow */}
      <section className="relative h-[600px] md:h-[680px] overflow-hidden">
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/85 via-teal-900/80 to-cyan-900/80" />
            <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
              <div
                className={`max-w-2xl transition-all duration-700 ${
                  i === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                  <slide.icon className="h-3.5 w-3.5 text-emerald-300" />
                  <span className="text-xs font-mono text-white/70 tracking-wider">
                    {String(i + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                  </span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
                  {slide.title}
                </h2>
                <p className="text-base md:text-lg text-white/70 mb-8 leading-relaxed max-w-xl">
                  {slide.desc}
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
                >
                  <Link2 className="h-4 w-4" />
                  Join the Program
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentSlide ? 'w-8 bg-emerald-400' : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Earnings Calculator */}
      <section className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm mb-4">
              <TrendingUp className="h-3.5 w-3.5 text-white" />
              <span className="text-xs font-mono text-white/80 tracking-wider">EARNINGS CALCULATOR</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Your Earning Potential</h2>
            <p className="text-white/70 text-sm max-w-xl mx-auto">Based on 25% recurring commission on the Pro plan ($149/mo) for the first 12 months. Agency and Enterprise referrals earn 30%. The more you refer, the more you earn.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {earningsTiers.map((tier) => (
              <div key={tier.referrals} className="bg-white/15 backdrop-blur-sm rounded-xl p-5 text-center border border-white/20 hover:bg-white/20 transition-all">
                <p className="text-3xl font-bold text-white">{tier.monthly}</p>
                <p className="text-[10px] text-white/60 uppercase tracking-wider font-mono mt-1">per month</p>
                <div className="mt-3 pt-3 border-t border-white/20">
                  <p className="text-lg font-semibold text-white/90">{tier.annual}</p>
                  <p className="text-[10px] text-white/50 uppercase tracking-wider font-mono mt-0.5">per year</p>
                </div>
                <p className="text-[10px] text-white/50 mt-2">{tier.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
            <UsersIcon className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-xs font-mono text-slate-500 tracking-wider">HOW IT WORKS</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">Four Steps to Earning</h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">From application to payout — the entire process is built into the platform.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {howItWorks.map((step) => (
            <div key={step.num} className="glass-card glass-card-hover p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
                <step.icon className="h-7 w-7 text-white" />
              </div>
              <p className="text-[10px] font-mono text-rose-500 mb-1">{step.num}</p>
              <h3 className="text-sm font-bold text-slate-800 mb-2">{step.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">Everything You Need to Earn</h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">A complete affiliate toolkit — tracking, analytics, and payouts.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {affiliateFeatures.map((f) => (
            <div key={f.title} className="glass-card p-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4 shadow-md">
                <f.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-sm font-bold text-slate-800 mb-2">{f.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Payout Methods */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="glass-card p-8 border border-emerald-200">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 mb-4">
              <PiggyBank className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-mono text-emerald-600 tracking-wider">PAYOUT METHODS</span>
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Get Paid via Stripe</h2>
            <p className="text-sm text-slate-400">Request withdrawals from A$50.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 max-w-xs mx-auto">
            {payoutMethods.map((m) => (
              <div key={m.label} className="flex items-center gap-3 p-5 rounded-xl bg-white/60 border border-slate-200/60 hover:border-emerald-200 transition-all">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${m.color} flex items-center justify-center shadow-md flex-shrink-0`}>
                  <m.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">{m.label}</p>
                  <p className="text-[10px] text-slate-400">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card mb-4">
              <Target className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-mono text-slate-500 tracking-wider">AFFILIATE PROGRAM</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-3">Start Earning Up to 30% Commission</h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">Apply to the affiliate program and start earning tiered recurring commission on every referral you bring to the platform. Starter 20%, Pro 25%, Agency and Enterprise 30% for the first 12 months.</p>
          </div>
          <LeadCaptureForm
            pageSource="affiliate"
            title="Apply to the Affiliate Program"
            subtitle="Tell us about yourself and how you plan to promote the platform"
            accentColor="emerald"
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Link2 className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-white">Aurelian Affiliate Program</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/login" className="text-xs text-slate-400 hover:text-white transition-colors">Sign In</Link>
            <Link to="/pricing" className="text-xs text-slate-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/legal" className="text-xs text-slate-400 hover:text-white transition-colors">Legal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SplashAffiliate;
